import { parseCsv } from "./csv"

/** Plantilla esperada del CSV — encabezados que reflejan el formulario de creación (AcademicInstitutionFormPage). */
export const ACADEMIC_INSTITUTION_CSV_TEMPLATE_HEADERS = ["Nombre", "Sector (publica/privada)", "Ciudad", "País"]

export const ACADEMIC_INSTITUTION_CSV_TEMPLATE_EXAMPLE_ROW = ["Colegio San José", "privada", "Bogotá", "Colombia"]

type FieldKey = "name" | "sector" | "city" | "country"

function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
}

const FIELD_MATCHERS: { key: FieldKey; test: (h: string) => boolean }[] = [
  { key: "name", test: (h) => h === "nombre" || h.startsWith("nombre") },
  { key: "sector", test: (h) => h.includes("sector") },
  { key: "city", test: (h) => h.includes("ciudad") },
  { key: "country", test: (h) => h === "pais" },
]

function detectColumns(header: string[]): Partial<Record<FieldKey, number>> {
  const columns: Partial<Record<FieldKey, number>> = {}
  const usedKeys = new Set<FieldKey>()
  header.forEach((headerCell, index) => {
    const normalized = normalize(headerCell)
    const matcher = FIELD_MATCHERS.find((m) => !usedKeys.has(m.key) && m.test(normalized))
    if (matcher) {
      columns[matcher.key] = index
      usedKeys.add(matcher.key)
    }
  })
  return columns
}

function cell(cells: string[], columns: Partial<Record<FieldKey, number>>, key: FieldKey): string {
  const index = columns[key]
  return index === undefined ? "" : (cells[index] ?? "").trim()
}

function resolveSector(rawValue: string): "public" | "private" | undefined {
  const target = normalize(rawValue)
  if (!target) return undefined
  if (["publica", "public", "pública"].includes(target)) return "public"
  if (["privada", "private"].includes(target)) return "private"
  return undefined
}

export type AcademicInstitutionRow = {
  name: string
  sector?: "public" | "private"
  city?: string
  country?: string
}

export type ParsedAcademicInstitutionRow = {
  line: number
  name: string
  error?: string
  warnings: string[]
  row: AcademicInstitutionRow
}

// Duplicados dentro del propio archivo se detectan por nombre normalizado —
// mismo criterio (trim + colapsar espacios + insensible a mayúsculas/acentos)
// que la unicidad aplicada en el backend (ver design.md, Decisión 1).
function dedupeKey(name: string): string {
  return name.replace(/\s+/g, " ").trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
}

export function parseAcademicInstitutionsCsv(text: string): { rows: ParsedAcademicInstitutionRow[]; error?: string } {
  const table = parseCsv(text)
  if (table.length === 0) {
    return { rows: [], error: "El archivo está vacío." }
  }

  const columns = detectColumns(table[0])
  if (columns.name === undefined) {
    return {
      rows: [],
      error: 'No se encontró la columna "Nombre" en la primera fila del CSV. Verifica que uses la plantilla esperada.',
    }
  }

  const seenNames = new Set<string>()

  const rows: ParsedAcademicInstitutionRow[] = table.slice(1).map((cells, i) => {
    const line = i + 2
    const name = cell(cells, columns, "name")
    const warnings: string[] = []

    if (!name) {
      return { line, name, error: "Falta el nombre de la institución", warnings, row: null as never }
    }

    const key = dedupeKey(name)
    if (seenNames.has(key)) {
      return { line, name, error: "Nombre duplicado dentro del mismo archivo", warnings, row: null as never }
    }
    seenNames.add(key)

    const sectorRaw = cell(cells, columns, "sector")
    const sector = sectorRaw ? resolveSector(sectorRaw) : undefined
    if (sectorRaw && !sector) warnings.push(`Sector no reconocido: "${sectorRaw}" (usa "pública" o "privada")`)

    const row: AcademicInstitutionRow = {
      name,
      sector,
      city: cell(cells, columns, "city") || undefined,
      country: cell(cells, columns, "country") || undefined,
    }

    return { line, name, warnings, row }
  })

  return { rows }
}
